import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';

import {
  isSignatureEmpty,
  shouldAddPoint,
  SignaturePoint,
  SignatureValue,
} from 'src/app/core/helpers/signature.helpers';

const INK = '#111111';

/**
 * Ekranda elle imza alanı — MASAK kimlik tespiti / nakit işlem beyanı formlarında müşteri (tüzel
 * kişide yetkilisi) fareyle, dokunmatik ekranda parmakla ya da kalemle imza atar.
 *
 * Çizim alanı her temada beyaz (kâğıt): imza siyah mürekkeple, basılan formdaki gibi görünür.
 * Noktalar CSS piksel koordinatlarında tutulur; sunucuya bu noktalar gider, SVG'yi sunucu üretir.
 *
 * Düzenlemede kayıtlı imza (sunucunun ürettiği SVG) gösterilir; "Yeniden İmzala" yeni çizim alanını
 * açar, "İmzayı Kaldır" kaydedince silinmek üzere işaretler.
 */
@Component({
  selector: 'app-signature-pad',
  templateUrl: './signature-pad.component.html',
  styles: [`
    .sig-paper { position: relative; background: #fff; border: 1px dashed #adb5bd; border-radius: 8px; height: 200px; overflow: hidden; }
    .sig-paper.disabled { opacity: .6; }
    .sig-paper canvas { position: absolute; top: 0; left: 0; width: 100%; height: 100%; touch-action: none; cursor: crosshair; }
    .sig-paper img { width: 100%; height: 100%; object-fit: contain; }
    .sig-line { position: absolute; left: 16px; right: 16px; bottom: 40px; height: 1px; background: #ced4da; pointer-events: none; }
    .sig-x { position: absolute; left: 16px; bottom: 42px; color: #adb5bd; font-size: 18px; line-height: 1; pointer-events: none; }
    .sig-placeholder { position: absolute; left: 0; right: 0; top: 70px; text-align: center; color: #adb5bd; pointer-events: none; }
  `],
})
export class SignaturePadComponent implements OnChanges {
  @Input() label = 'Müşteri İmzası';
  @Input() description = 'Müşteri (tüzel kişide yetkilisi) aşağıdaki alana fareyle, parmakla ya da kalemle imza atar.';
  @Input() disabled = false;
  /** Düzenlemede kayıtlı imza (SVG); yoksa null. */
  @Input() existingSvg?: string | null;
  /** Kayıtlı imzanın kaldırılmak üzere işaretli olduğu. */
  @Input() existingRemoved = false;
  @Output() existingRemovedChange = new EventEmitter<boolean>();
  /** Yeni çizim; null = çizilmedi. */
  @Output() valueChange = new EventEmitter<SignatureValue | null>();

  strokes: SignaturePoint[][] = [];
  /** Kayıtlı imza varken çizim alanı "Yeniden İmzala" ile açılır. */
  redrawing = false;
  existingSvgUrl?: SafeUrl;
  private value: SignatureValue | null = null;

  private canvas?: HTMLCanvasElement;
  private drawing = false;

  constructor(private sanitizer: DomSanitizer) {}

  /** Çizim alanı *ngIf ile açılıp kapandığı için ViewChild setter'ı: her açılışta boyutlanır. */
  @ViewChild('canvas')
  set canvasRef(ref: ElementRef<HTMLCanvasElement> | undefined) {
    this.canvas = ref?.nativeElement;
    if (this.canvas) setTimeout(() => this.resizeAndRedraw());
  }

  get hasExisting(): boolean {
    return !!this.existingSvg && !this.existingRemoved;
  }

  get showPad(): boolean {
    return !this.hasExisting || this.redrawing;
  }

  get tooShort(): boolean {
    return this.strokes.some((s) => s.length > 0) && isSignatureEmpty(this.value);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.existingSvg) {
      // SVG sunucuda kalem izlerinden üretilir; <img> içinde betik zaten çalışmaz. Angular'ın URL
      // temizleyicisi data:image/svg+xml'i kabul etmediği için açıkça güvenilir işaretlenir.
      this.existingSvgUrl = this.existingSvg
        ? this.sanitizer.bypassSecurityTrustUrl('data:image/svg+xml;charset=utf-8,' + encodeURIComponent(this.existingSvg))
        : undefined;
    }
  }

  @HostListener('window:resize')
  onResize() {
    this.resizeAndRedraw();
  }

  pointerDown(e: PointerEvent) {
    if (this.disabled || !this.canvas || (e.pointerType === 'mouse' && e.button !== 0)) return;
    e.preventDefault();
    this.canvas.setPointerCapture(e.pointerId);
    this.drawing = true;
    this.strokes.push([[e.offsetX, e.offsetY]]);
    this.redraw();
  }

  pointerMove(e: PointerEvent) {
    if (!this.drawing) return;
    e.preventDefault();
    const stroke = this.strokes[this.strokes.length - 1];
    const next: SignaturePoint = [e.offsetX, e.offsetY];
    if (!stroke || !shouldAddPoint(stroke[stroke.length - 1], next)) return;
    const last = stroke[stroke.length - 1];
    stroke.push(next);
    // Yalnızca yeni parça çizilir; tam yeniden çizim geri al/temizle/boyut değişiminde.
    const ctx = this.context();
    if (ctx) {
      ctx.beginPath();
      ctx.moveTo(last[0], last[1]);
      ctx.lineTo(next[0], next[1]);
      ctx.stroke();
    }
  }

  pointerUp(e: PointerEvent) {
    if (!this.drawing) return;
    this.drawing = false;
    if (this.canvas?.hasPointerCapture(e.pointerId)) this.canvas.releasePointerCapture(e.pointerId);
    this.emit();
  }

  undo() {
    this.strokes = this.strokes.slice(0, -1);
    this.redraw();
    this.emit();
  }

  clear() {
    this.strokes = [];
    this.redraw();
    this.emit();
  }

  removeExisting() {
    this.existingRemovedChange.emit(true);
  }

  restoreExisting() {
    this.existingRemovedChange.emit(false);
  }

  keepExisting() {
    this.clear();
    this.redrawing = false;
  }

  private emit() {
    const strokes = this.strokes.filter((s) => s.length > 0).map((s) => [...s]);
    this.value = strokes.length && this.canvas
      ? { strokes, width: this.canvas.clientWidth, height: this.canvas.clientHeight }
      : null;
    this.valueChange.emit(this.value);
  }

  private context(): CanvasRenderingContext2D | null {
    const ctx = this.canvas?.getContext('2d') ?? null;
    if (ctx) {
      ctx.strokeStyle = INK;
      ctx.lineWidth = 2.5;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    }
    return ctx;
  }

  /** Tuval piksel yoğunluğuna göre boyutlanır (keskin çizgi); noktalar CSS pikselinde kalır. */
  private resizeAndRedraw() {
    if (!this.canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const width = this.canvas.clientWidth;
    const height = this.canvas.clientHeight;
    if (!width || !height) return;
    this.canvas.width = Math.round(width * dpr);
    this.canvas.height = Math.round(height * dpr);
    this.canvas.getContext('2d')?.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.redraw();
  }

  private redraw() {
    const ctx = this.context();
    if (!ctx || !this.canvas) return;
    ctx.clearRect(0, 0, this.canvas.clientWidth, this.canvas.clientHeight);
    for (const stroke of this.strokes) {
      if (!stroke.length) continue;
      ctx.beginPath();
      ctx.moveTo(stroke[0][0], stroke[0][1]);
      if (stroke.length === 1) ctx.lineTo(stroke[0][0] + 0.1, stroke[0][1]);
      for (let i = 1; i < stroke.length; i++) ctx.lineTo(stroke[i][0], stroke[i][1]);
      ctx.stroke();
    }
  }
}
